import { useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import useLocalStorage from "use-local-storage";
import moment from "moment/moment";
import styles from "./ShowTenantList.module.css";
// @ts-expect-error pure js
import { ProfileStatus } from "../../models/profile";
// @ts-expect-error pure js
import { TenantStatus } from "../../models/tenant_old.js";
// @ts-expect-error pure js
import { isPropertyDisabled } from "../../utils/properties";
// @ts-expect-error pure js
import useProperties from "../../hooks/use-properties";
// @ts-expect-error pure js
import useProfileBailleurs from "../../hooks/use-profile-bailleurs";
// @ts-expect-error pure js
import { useSumBalanceTenants } from "../../hooks/use-balances";
// @ts-expect-error pure js
import IsLoading from "../UI/IsLoading";
// @ts-expect-error pure js
import useMediaQuery from "../../hooks/use-media-query";
// @ts-expect-error pure js
import useAuthenticatedAxios from "../../hooks/use-authenticated-axios";
// @ts-expect-error pure js
import DocumentDownloader from "../UI/DocumentDownloader";
// @ts-expect-error pure js
import { handleAPIError, classnames } from "../../utils/index.jsx";
import { useToast } from "../UI/Toast/useToast";
import { Table } from "../UI/Table";
import useTableSort from "../../hooks/use-table-sort";
import {
  IconArchive,
  IconFileExport,
  IconHome,
  IconPlus,
  IconUsers,
} from "@tabler/icons-react";
import EmptyState from "../UI/EmptyState.tsx";
import UIButton from "../UI/Button.tsx";
import Card from "../UI/Card.tsx";
import AvatarStack from "../UI/AvatarStack.tsx";
import { getInitials } from "../Profile/profileUtils.ts";
import type { Profile, ProfileBailleur } from "../Profile/types.ts";
import Text from "../UI/Text";
import Status from "../UI/Status.tsx";
import { Button } from "@base-ui/react/button";
import { getStatusInfo } from "./tenantStatus";
import AplTag from "./Apl/AplTag";
import type { Tenant } from "../../models/tenant";
import { PropertyStatus, type Property } from "../../models/property";
import type { Balance } from "../../models/balance";
import FilterBar from "../UI/FilterBar/FilterBar.tsx";
import FilterSection, {
  FilterSelectSection,
} from "../UI/FilterBar/FilterSection.tsx";

interface TenantRowProps {
  tenant: Tenant;
  profiles: Profile[];
  property: Property | undefined;
  balance: Balance | undefined;
  onClick?: () => void;
}

function isPropertyArchived(property: Property | undefined): boolean {
  return property?.status === PropertyStatus.Archive.status;
}

// Un locataire d'un bien archivé ne reçoit plus rien : son état de paiement
// n'a plus de sens, on affiche l'archivage à la place.
function TenantStatusBadge({
  tenant,
  property,
  balance,
}: Pick<TenantRowProps, "tenant" | "property" | "balance">) {
  if (isPropertyArchived(property)) {
    return <Status color="muted" icon={IconArchive} text="Bien archivé" />;
  }
  const statusInfo = getStatusInfo(balance, tenant);
  return statusInfo ? (
    <Status color={statusInfo.status} text={statusInfo.text} />
  ) : null;
}

type SortKey = "nom" | "bien" | "duree";

const STATUS_OPTIONS = [
  { value: "a_jour", label: "À jour" },
  { value: "retard", label: "En retard" },
  { value: "impaye", label: "Impayé" },
  { value: "preavis", label: "En préavis" },
];

const APL_OPTIONS = [{ value: "apl", label: "Perçoit une aide (APL)" }];

function TenantIdentity({
  tenant,
  profiles,
  propertyName,
}: {
  tenant: Tenant;
  profiles: Profile[];
  propertyName?: string;
}) {
  return (
    <div className={styles.tenantIdentity}>
      <AvatarStack
        items={profiles}
        getInitials={getInitials}
        getKey={(_, index) => index}
      />
      <div>
        <div className={styles.nameRow}>
          <Text as="div" weight="bold">
            {tenant.nom}
          </Text>
          <AplTag tenant={tenant} />
        </div>
        {propertyName && (
          <Text as="div" variant="deci">
            {propertyName}
          </Text>
        )}
      </div>
    </div>
  );
}

function TenantRowDesktop({
  tenant,
  profiles,
  property,
  balance,
  onClick,
}: TenantRowProps) {
  return (
    <Table.Row
      onClick={onClick}
      className={classnames(
        styles.desktopRow,
        isPropertyArchived(property) && styles.archivedRow,
      )}
    >
      <Table.Cell>
        <TenantIdentity tenant={tenant} profiles={profiles} />
      </Table.Cell>

      <Table.Cell>
        <Text as="div" className={styles.propertyCell}>
          <IconHome size={16} />
          {property?.nom}
        </Text>
      </Table.Cell>

      <Table.Cell>
        <Text
          as="div"
          variant="deci"
          className={tenant.preavis ? styles.preavis : undefined}
        >
          {moment(tenant.debut).format("D MMM YYYY")} -{" "}
          {moment(tenant.fin).format("D MMM YYYY")}
        </Text>
      </Table.Cell>

      <Table.Cell>
        <TenantStatusBadge
          tenant={tenant}
          property={property}
          balance={balance}
        />
      </Table.Cell>
    </Table.Row>
  );
}

function TenantRowMobile({
  tenant,
  profiles,
  property,
  balance,
}: Omit<TenantRowProps, "onClick">) {
  const nav = useNavigate();

  return (
    <Card
      padding="sm"
      as={Button}
      onClick={() => nav(`/bailleur/tenants/${tenant.id}`)}
      className={classnames(
        styles.mobileCard,
        isPropertyArchived(property) && styles.archivedRow,
      )}
    >
      <TenantIdentity
        tenant={tenant}
        profiles={profiles}
        propertyName={property?.nom}
      />
      <Text
        as="div"
        variant="deci"
        className={tenant.preavis ? styles.preavis : undefined}
      >
        Durée du bail : {moment(tenant.debut).format("D MMM YYYY")} -{" "}
        {moment(tenant.fin).format("D MMM YYYY")}
      </Text>
      <Text as="span" variant="deci">
        Statut :
      </Text>{" "}
      <TenantStatusBadge
        tenant={tenant}
        property={property}
        balance={balance}
      />
    </Card>
  );
}

interface ShowTenantListProps {
  tenants: Tenant[];
  isArchived?: boolean;
  isLoading: boolean;
  onAdd?: () => void;
  addLabel?: string;
}

function getStatusKey(
  balance: Balance | undefined,
  tenant: Tenant,
): "a_jour" | "retard" | "impaye" | null {
  const info = getStatusInfo(balance, tenant);
  if (!info) return null;
  if (info.text === "À jour") return "a_jour";
  if (info.text === "Retard") return "retard";
  if (info.text === "Impayé") return "impaye";
  return null;
}

export default function ShowTenantList({
  tenants,
  isArchived,
  isLoading,
  onAdd,
  addLabel,
}: ShowTenantListProps) {
  const isSmallScreen = useMediaQuery("(max-width: 799px)");
  const navigate = useNavigate();
  const axios = useAuthenticatedAxios();
  const toast = useToast();
  const { properties } = useProperties();
  const { profileBailleurs } = useProfileBailleurs();
  const { sumBalances } = useSumBalanceTenants();
  const disabledPropertyIds = new Set(
    properties
      .filter(
        (prop: Property) =>
          isPropertyDisabled(prop) || isPropertyArchived(prop),
      )
      .map((prop: Property) => prop.id),
  );

  const [search, setSearch] = useState("");
  const storagePrefix = isArchived
    ? "tenantList_archived"
    : "tenantList_actifs";
  const [statusFilter, setStatusFilter] = useLocalStorage<string[]>(
    `${storagePrefix}_statusFilter`,
    [],
  );
  const [bailleurFilter, setBailleurFilter] = useLocalStorage<string[]>(
    `${storagePrefix}_bailleurFilter`,
    [],
  );
  const [bienFilter, setBienFilter] = useLocalStorage<string[]>(
    `${storagePrefix}_bienFilter`,
    [],
  );
  const [aplFilter, setAplFilter] = useLocalStorage<string[]>(
    `${storagePrefix}_aplFilter`,
    [],
  );
  const { sort, toggleSort, directionFor } = useTableSort<SortKey>(
    `${storagePrefix}_sort`,
  );

  const sortedTenants = tenants
    ? [...tenants].sort((a, b) =>
        disabledPropertyIds.has(a.id_bien) && disabledPropertyIds.has(b.id_bien)
          ? 0
          : disabledPropertyIds.has(a.id_bien)
          ? 1
          : -1,
      )
    : [];

  function getFilteredProfiles(tenant: Tenant): Profile[] {
    if (isArchived) {
      return tenant.profiles.filter(
        (profile) =>
          profile.garantor_of === null &&
          (tenant.status === TenantStatus.Archive.status ||
            profile.status === ProfileStatus.Archive.status),
      );
    } else {
      return tenant.profiles.filter(
        (profile) =>
          profile.garantor_of === null &&
          profile.status === ProfileStatus.Actif.status,
      );
    }
  }

  const tenantsWithMeta = useMemo(
    () =>
      sortedTenants
        .map((tenant) => ({
          tenant,
          profiles: getFilteredProfiles(tenant),
          property: properties?.find(
            (prop: Property) => prop.id === tenant.id_bien,
          ),
          balance: sumBalances?.find(
            (bal: Balance) => bal.id_tenant === tenant.id,
          ),
        }))
        .filter(({ profiles }) => profiles.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortedTenants, properties, sumBalances, isArchived],
  );

  const bailleurOptions = useMemo(
    () =>
      [...(profileBailleurs ?? [])]
        .sort((a: ProfileBailleur, b: ProfileBailleur) =>
          a.nom_profile.localeCompare(b.nom_profile),
        )
        .map((pb: ProfileBailleur) => ({
          value: String(pb.id),
          label: pb.nom_profile,
        })),
    [profileBailleurs],
  );

  // Purge les valeurs stockées qui ne correspondent plus à une option
  // (bien archivé, profil supprimé) pour éviter un filtre invisible.
  const activeBailleurFilter = useMemo(
    () =>
      bailleurFilter.filter((value) =>
        bailleurOptions.some((opt) => opt.value === value),
      ),
    [bailleurFilter, bailleurOptions],
  );

  // Seuls les biens des locataires restants après filtre bailleur sont proposés.
  const bienOptions = useMemo(() => {
    const bienIds = new Set(
      tenantsWithMeta
        .filter(
          ({ property }) =>
            activeBailleurFilter.length === 0 ||
            activeBailleurFilter.includes(
              String(property?.id_profile_nom_owner),
            ),
        )
        .map(({ tenant }) => tenant.id_bien),
    );
    return [...((properties ?? []) as Property[])]
      .filter((prop) => bienIds.has(prop.id))
      .sort((a, b) => a.nom.localeCompare(b.nom))
      .map((prop) => ({ value: String(prop.id), label: prop.nom }));
  }, [properties, tenantsWithMeta, activeBailleurFilter]);

  const activeBienFilter = useMemo(
    () =>
      bienFilter.filter((value) =>
        bienOptions.some((opt) => opt.value === value),
      ),
    [bienFilter, bienOptions],
  );

  const filteredTenants = useMemo(() => {
    const searchLower = search.toLowerCase();
    return tenantsWithMeta.filter(({ tenant, profiles, property, balance }) => {
      if (searchLower) {
        const matchesSearch =
          tenant.nom?.toLowerCase().includes(searchLower) ||
          property?.nom?.toLowerCase().includes(searchLower) ||
          profiles.some(
            (p) =>
              p.mail?.toLowerCase().includes(searchLower) ||
              p.tel?.toLowerCase().includes(searchLower),
          );
        if (!matchesSearch) return false;
      }
      if (statusFilter && statusFilter.length > 0) {
        let matches = false;
        if (statusFilter.includes("preavis") && tenant.preavis) {
          matches = true;
        }
        const statusKey = getStatusKey(balance, tenant);
        if (statusKey && statusFilter.includes(statusKey)) {
          matches = true;
        }
        if (!matches) return false;
      }
      if (
        activeBailleurFilter.length > 0 &&
        !activeBailleurFilter.includes(String(property?.id_profile_nom_owner))
      ) {
        return false;
      }
      if (
        activeBienFilter.length > 0 &&
        !activeBienFilter.includes(String(tenant.id_bien))
      ) {
        return false;
      }
      if (aplFilter.includes("apl") && !tenant.caf_aide) {
        return false;
      }
      return true;
    });
  }, [
    tenantsWithMeta,
    search,
    statusFilter,
    activeBailleurFilter,
    activeBienFilter,
    aplFilter,
  ]);

  const displayedTenants = useMemo(() => {
    if (!sort) return filteredTenants;
    const dir = sort.direction === "asc" ? 1 : -1;
    return [...filteredTenants].sort((a, b) => {
      if (sort.key === "duree") {
        return dir * moment(a.tenant.debut).diff(moment(b.tenant.debut));
      }
      const [valA, valB] =
        sort.key === "nom"
          ? [a.tenant.nom, b.tenant.nom]
          : [a.property?.nom, b.property?.nom];
      return (
        dir *
        (valA ?? "").localeCompare(valB ?? "", "fr", { sensitivity: "base" })
      );
    });
  }, [filteredTenants, sort]);

  const activeFiltersCount =
    (statusFilter.length > 0 ? 1 : 0) +
    (activeBailleurFilter.length > 0 ? 1 : 0) +
    (activeBienFilter.length > 0 ? 1 : 0) +
    (aplFilter.length > 0 ? 1 : 0);

  function clearFilters() {
    setStatusFilter([]);
    setBailleurFilter([]);
    setBienFilter([]);
    setAplFilter([]);
  }

  if (isLoading) return <IsLoading />;

  if (tenantsWithMeta.length === 0) {
    return isArchived ? (
      <EmptyState
        icon={IconArchive}
        title="Aucun locataire archivé"
        description="Les locataires dont le bail est terminé apparaîtront ici."
      />
    ) : (
      <EmptyState
        icon={IconUsers}
        title="Aucun locataire pour le moment"
        description="Ajoutez votre premier locataire pour créer son bail, suivre ses paiements et envoyer ses quittances."
      >
        {onAdd && (
          <UIButton icon={IconPlus} onClick={onAdd}>
            {addLabel}
          </UIButton>
        )}
      </EmptyState>
    );
  }

  const filterBarActions = (
    <DocumentDownloader className={styles.leftActions}>
      {(setSignedUrl: (url: string) => void) => (
        <>
          {onAdd && (
            <UIButton icon={IconPlus} onClick={onAdd}>
              {addLabel}
            </UIButton>
          )}
          <UIButton
            variant="secondary"
            icon={IconFileExport}
            disabled={filteredTenants.length === 0}
            onClick={async () => {
              try {
                const response = await axios.post("/tenants/export", {
                  tenant_ids: filteredTenants.map(({ tenant }) => tenant.id),
                });
                setSignedUrl(response.data);
              } catch (error) {
                toast.error({
                  title: "Impossible d'exporter les locataires",
                  description: handleAPIError(error),
                });
              }
            }}
          >
            Exporter
          </UIButton>
        </>
      )}
    </DocumentDownloader>
  );

  const filterBar = (
    <FilterBar
      searchValue={search}
      onSearchChange={setSearch}
      searchPlaceholder="Rechercher un locataire"
      activeFiltersCount={activeFiltersCount}
      onClearFilters={clearFilters}
      leftActions={filterBarActions}
    >
      <FilterSection
        title="Statut"
        options={STATUS_OPTIONS}
        values={statusFilter}
        onChange={setStatusFilter}
        defaultOpen
      />
      <FilterSelectSection
        title="Profil bailleur"
        options={bailleurOptions}
        values={activeBailleurFilter}
        onChange={setBailleurFilter}
      />
      <FilterSelectSection
        title="Bien"
        options={bienOptions}
        values={activeBienFilter}
        onChange={setBienFilter}
      />
      <FilterSection
        title="Aides au logement"
        options={APL_OPTIONS}
        values={aplFilter}
        onChange={setAplFilter}
      />
    </FilterBar>
  );

  if (filteredTenants.length === 0) {
    return (
      <>
        {filterBar}
        <Card padding="lg" className={styles.emptyState}>
          Aucun locataire trouvé
        </Card>
      </>
    );
  }

  if (isSmallScreen) {
    return (
      <>
        {filterBar}
        <div className={styles.mobileContainer}>
          {displayedTenants.map(({ tenant, profiles, property, balance }) => (
            <TenantRowMobile
              key={tenant.id}
              tenant={tenant}
              profiles={profiles}
              property={property}
              balance={balance}
            />
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      {filterBar}
      <Table variant="card">
        <Table.Header>
          <Table.Column
            sortDirection={directionFor("nom")}
            onSort={() => toggleSort("nom")}
          >
            Nom du locataire
          </Table.Column>
          <Table.Column
            sortDirection={directionFor("bien")}
            onSort={() => toggleSort("bien")}
          >
            Bien affecté
          </Table.Column>
          <Table.Column
            sortDirection={directionFor("duree")}
            onSort={() => toggleSort("duree")}
          >
            Durée du bail
          </Table.Column>
          <Table.Column>Statut</Table.Column>
        </Table.Header>
        <Table.Body>
          {displayedTenants.map(({ tenant, profiles, property, balance }) => (
            <TenantRowDesktop
              key={tenant.id}
              tenant={tenant}
              profiles={profiles}
              property={property}
              balance={balance}
              onClick={() => navigate(`/bailleur/tenants/${tenant.id}`)}
            />
          ))}
        </Table.Body>
      </Table>
    </>
  );
}
